import React from "react";
import { Link } from "react-router-dom";
import { Baby, Syringe, Stethoscope, Truck, StickyNote, Skull, ShoppingCart, Sparkles, Beef, Ban, AlertTriangle, Droplets, Droplet, Sun, Scissors, Pill, ArrowLeftRight, HeartPulse } from "lucide-react";
import { formatDateTime } from "@/lib/utils";
import { cn, formatDate } from "@/lib/utils";

const CONFIG = {
  birth:           { icon: Baby,         label: "Alta / Nacimiento",  tone: "present" },
  purchase:        { icon: ShoppingCart, label: "Compra",             tone: "present" },
  vaccination:     { icon: Syringe,      label: "Vacunación",          tone: "present" },
  palpation:       { icon: Stethoscope,  label: "Palpación",           tone: "warm"    },
  transfer:        { icon: Truck,        label: "Traslado",            tone: "ochre"   },
  observation:     { icon: StickyNote,   label: "Observación",         tone: "neutral" },
  death:           { icon: Skull,        label: "Muerte",              tone: "missing" },
  slaughter:       { icon: Beef,         label: "Sacrificio",          tone: "missing" },
  sale:            { icon: ShoppingCart, label: "Venta",               tone: "ochre"   },
  lost:            { icon: Ban,          label: "Declarada extraviada", tone: "ochre"  },
  abortion:        { icon: AlertTriangle,label: "Aborto",              tone: "missing" },
  milking_in:      { icon: Droplets,     label: "Ingreso a ordeño",    tone: "present" },
  milk_production: { icon: Droplets,     label: "Producción de leche", tone: "present" },
  drying:          { icon: Droplet,      label: "Salida de ordeño (secado)", tone: "warm" },
  escotero:        { icon: Sun,          label: "Ingreso a escotero",  tone: "ochre"   },
  weaning:         { icon: Scissors,     label: "Destete",             tone: "warm"    },
  ai_report:       { icon: Sparkles,     label: "Reporte IA",          tone: "warm"    },
  treatment:       { icon: Pill,         label: "Tratamiento / patología", tone: "missing" },
  // Fix Q (2026-10-21)
  calving:         { icon: Baby,         label: "Parto",               tone: "present" },
  lot_change:      { icon: ArrowLeftRight, label: "Cambio de lote",    tone: "ochre"   },
  health_ok:       { icon: HeartPulse,   label: "Marcado como sano",   tone: "present" },
};

const TONE = {
  present:  "bg-[hsl(var(--state-present-bg))] text-[hsl(var(--state-present))]",
  missing:  "bg-[hsl(var(--state-missing-bg))] text-[hsl(var(--state-missing))]",
  ochre:    "bg-[hsl(var(--brand-ochre)/0.16)] text-[hsl(var(--brand-ochre))]",
  warm:     "bg-[hsl(var(--brand-warm)/0.14)]  text-[hsl(var(--brand-warm))]",
  neutral:  "bg-muted text-muted-foreground",
};

export function TimelineEvent({ event }) {
  const cfg = CONFIG[event.type] || CONFIG.observation;
  const Icon = cfg.icon;
  return (
    <div
      className="relative flex gap-4 pb-5 last:pb-0"
      data-testid={`timeline-event-${event.type}`}
    >
      {/* Vertical hairline */}
      <div className="flex flex-col items-center">
        <div className={cn(
          "w-9 h-9 rounded-full grid place-items-center shrink-0",
          TONE[cfg.tone] || TONE.neutral
        )}>
          <Icon size={15} />
        </div>
        <div className="flex-1 w-px bg-border mt-1" />
      </div>
      <div className="min-w-0 flex-1 pb-1">
        <div className="flex items-baseline justify-between gap-2 flex-wrap">
          <span className="font-heading font-semibold text-[14px] text-foreground tracking-tight">{cfg.label}</span>
          <span className="text-[10.5px] font-mono text-muted-foreground tabular-nums">
            {formatDateTime(event.date)}
          </span>
        </div>
        {event.payload?.note && <p className="mt-1 text-[13px] text-foreground/80 leading-relaxed">{event.payload.note}</p>}
        {event.payload?.summary && !event.payload?.note && event.type !== "palpation" && event.type !== "vaccination" && (
          <p className="mt-1 text-[13px] text-foreground/80 leading-relaxed">{event.payload.summary}</p>
        )}
        {event.type === "palpation" && (
          <p className="mt-1 text-[13px] text-foreground/80 leading-relaxed">
            {event.payload?.diagnosis
              ? (event.payload.diagnosis === "pregnant" ? `Preñada${event.payload.months ? ` · ${event.payload.months} meses` : ""}`
                : event.payload.diagnosis === "empty" ? "Vacía" : "Vacía descarte")
              : event.payload?.level ? <>Nivel <span className="font-mono">{event.payload.level}/9</span></> : null}
            {event.payload?.diagnosis && event.payload?.level ? <> · Nivel <span className="font-mono">{event.payload.level}/9</span></> : null}
            {event.payload?.vet_notes ? <> — {event.payload.vet_notes}</> : null}
            {event.payload?.empty_note ? <> — {event.payload.empty_note}</> : null}
            {event.payload?.cull_note ? <> — {event.payload.cull_note}</> : null}
            {event.payload?.vet ? <span className="text-muted-foreground"> · {event.payload.vet}</span> : null}
            {event.payload?.due_date && (
              <span className="block text-[12px] text-[hsl(var(--state-present))] mt-0.5">
                Parto estimado: {formatDate(event.payload.due_date)}
              </span>
            )}
            {event.payload?.registered_by_name && (
              <span className="block text-[11px] text-muted-foreground mt-0.5">
                Palpación registrada por {event.payload.registered_by_name}
              </span>
            )}
          </p>
        )}
        {event.type === "vaccination" && (
          <p className="mt-1 text-[13px] text-foreground/80">
            {event.payload?.vaccine_name || ""}{event.payload?.dose ? ` · ${event.payload.dose}` : ""}
          </p>
        )}
        {event.type === "ai_report" && event.payload?.response_markdown && (
          <p className="mt-2 text-[12.5px] text-muted-foreground italic line-clamp-3 leading-relaxed">
            “{event.payload.response_markdown.slice(0, 220)}…”
          </p>
        )}
        {event.type === "calving" && (
          <dl className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1 text-[12.5px]" data-testid="timeline-calving-detail">
            <Row k="Cría" v={<Link to={`/animales/${event.payload?.calf_id}`} className="text-primary hover:underline">Lomo {event.payload?.calf_lomo || "—"}</Link>} />
            {event.payload?.calf_sex && <Row k="Sexo" v={event.payload.calf_sex === "female" ? "Hembra" : "Macho"} />}
            {event.payload?.calf_chip && <Row k="Chip de la cría" v={<span className="font-mono">{event.payload.calf_chip}</span>} full />}
            {event.payload?.registered_at && <Row k="Cría registrada" v={formatDateTime(event.payload.registered_at)} full />}
          </dl>
        )}
        {event.type === "treatment" && (
          <dl className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1 text-[12.5px]" data-testid="timeline-treatment-detail">
            {event.payload?.pathology && <Row k="Patología" v={event.payload.pathology} />}
            {event.payload?.medication && <Row k="Medicamento" v={event.payload.medication} />}
            {event.payload?.dose && <Row k="Dosis" v={event.payload.dose} />}
            {event.payload?.notes && <Row k="Notas" v={event.payload.notes} full />}
          </dl>
        )}
        {event.type === "sale" && (
          <dl className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1 text-[12.5px]" data-testid="timeline-sale-detail">
            {event.payload?.kind === "sale_breeding" ? (
              <>
                {(event.payload?.buyer || event.payload?.destination) && <Row k="Comprador" v={event.payload.buyer || event.payload.destination} />}
                {event.payload?.dest_farm_name && <Row k="Finca destino" v={event.payload.dest_farm_name} />}
              </>
            ) : (
              event.payload?.matadero_name && <Row k="Matadero" v={event.payload.matadero_name} />
            )}
            {event.payload?.address && <Row k="Dirección" v={event.payload.address} full />}
            {event.payload?.guide_number && <Row k="Guía INSAI" v={event.payload.guide_number} />}
            {event.payload?.guide_date && <Row k="Fecha guía" v={event.payload.guide_date} />}
            {event.payload?.driver && <Row k="Chofer" v={event.payload.driver} />}
            {event.payload?.driver_id && <Row k="Cédula chofer" v={event.payload.driver_id} />}
            {event.payload?.plate && <Row k="Placa" v={event.payload.plate} />}
            {event.payload?.weight_kg !== undefined && event.payload?.weight_kg !== null && <Row k="Peso" v={`${event.payload.weight_kg} kg`} />}
          </dl>
        )}
        {event.type === "transfer" && (
          <dl className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1 text-[12.5px]" data-testid="timeline-transfer-detail">
            {event.payload?.driver && <Row k="Chofer" v={event.payload.driver} />}
            {event.payload?.driver_id && <Row k="Cédula chofer" v={event.payload.driver_id} />}
            {event.payload?.plate && <Row k="Placa" v={event.payload.plate} />}
            {event.payload?.verification && <Row k="Verificación" v={event.payload.verification === "ok" ? "Coincide" : "No coincide"} />}
            {event.payload?.arrival_weight !== undefined && event.payload?.arrival_weight !== null && event.payload?.arrival_weight !== "" && <Row k="Peso al llegar" v={`${event.payload.arrival_weight} kg`} />}
            {event.payload?.notes && <Row k="Notas" v={event.payload.notes} full />}
          </dl>
        )}
        {event.type === "abortion" && (
          <dl className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1 text-[12.5px]" data-testid="timeline-abortion-detail">
            {event.payload?.fetus_sex && <Row k="Género del feto" v={event.payload.fetus_sex === "female" ? "Hembra" : "Macho"} />}
            {event.payload?.fetus_hair && <Row k="¿Pelo?" v={SI_NO(event.payload.fetus_hair)} />}
            {event.payload?.fetus_features && <Row k="¿Orejas/cola/hocico?" v={SI_NO(event.payload.fetus_features)} />}
            {event.payload?.placenta_found && <Row k="¿Se encontró placenta?" v={SI_NO(event.payload.placenta_found)} />}
            {event.payload?.placenta_retained && <Row k="¿Placenta retenida?" v={SI_NO(event.payload.placenta_retained)} />}
            {event.payload?.necrosis && <Row k="¿Necrosis/mal olor?" v={SI_NO(event.payload.necrosis)} />}
            {event.payload?.pus && <Row k="¿Pus?" v={SI_NO(event.payload.pus)} />}
            {event.payload?.placenta_color && <Row k="Color placenta" v={event.payload.placenta_color === "yellow" ? "Amarillo" : "Gris"} />}
          </dl>
        )}
      </div>
    </div>
  );
}

function SI_NO(v) { return v === "yes" ? "Sí" : v === "no" ? "No" : v; }

function Row({ k, v, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <span className="text-muted-foreground">{k}:</span> <span className="text-foreground/85">{v}</span>
    </div>
  );
}
